import { getCsrfToken } from '@/integrations/php/client';

export interface DeliveryReadiness {
  configured: boolean;
  sent: number;
  failed: number;
  queued: number;
  lastSentAt: string | null;
  lastFailedAt: string | null;
}

export interface AdminSettingsSnapshot {
  generatedAt: string | null;
  configurationUpdatedAt: string | null;
  readiness: {
    ready: number;
    total: number;
    attention: number;
  };
  email: DeliveryReadiness & {
    senderName: string;
    senderEmail: string;
    transport: string;
  };
  sms: DeliveryReadiness & {
    quietHours: string;
  };
  maps: {
    configured: boolean;
    activePartners: number;
    geocodedPartners: number;
  };
  meta: {
    pixelConfigured: boolean;
    capiConfigured: boolean;
    testMode: boolean;
    analyticsConfigured: boolean;
    crmSent: number;
    crmFailed: number;
    crmQueued: number;
    lastCrmSentAt: string | null;
  };
  partnerAccess: {
    registrationGateConfigured: boolean;
    activePartners: number;
    credentialedPartners: number;
    exactLeadLinksRequireLogin: boolean;
  };
  protection: {
    plaintextSecretsReturned: boolean;
    credentialRotation: string;
  };
}

export interface UpdateSenderIdentityInput {
  senderName: string;
  senderEmail: string;
}

export class AdminSettingsApiError extends Error {
  readonly fields: Record<string, string>;

  constructor(message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'AdminSettingsApiError';
    this.fields = fields;
  }
}

const number = (value: unknown): number => Number(value || 0);
const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? value as Record<string, unknown> : {};

function normalizeDelivery(value: unknown): DeliveryReadiness {
  const delivery = record(value);
  return {
    configured: Boolean(delivery.configured),
    sent: number(delivery.sent),
    failed: number(delivery.failed),
    queued: number(delivery.queued),
    lastSentAt: nullableString(delivery.last_sent_at),
    lastFailedAt: nullableString(delivery.last_failed_at),
  };
}

function normalizeSettings(value: unknown): AdminSettingsSnapshot {
  const settings = record(value);
  const readiness = record(settings.readiness);
  const email = record(settings.email);
  const sms = record(settings.sms);
  const maps = record(settings.maps);
  const meta = record(settings.meta);
  const partnerAccess = record(settings.partner_access);
  const protection = record(settings.protection);

  return {
    generatedAt: nullableString(settings.generated_at),
    configurationUpdatedAt: nullableString(settings.configuration_updated_at),
    readiness: {
      ready: number(readiness.ready),
      total: number(readiness.total),
      attention: number(readiness.attention),
    },
    email: {
      ...normalizeDelivery(email),
      senderName: String(email.sender_name || ''),
      senderEmail: String(email.sender_email || ''),
      transport: String(email.transport || ''),
    },
    sms: {
      ...normalizeDelivery(sms),
      quietHours: String(sms.quiet_hours || ''),
    },
    maps: {
      configured: Boolean(maps.configured),
      activePartners: number(maps.active_partners),
      geocodedPartners: number(maps.geocoded_partners),
    },
    meta: {
      pixelConfigured: Boolean(meta.pixel_configured),
      capiConfigured: Boolean(meta.capi_configured),
      testMode: Boolean(meta.test_mode),
      analyticsConfigured: Boolean(meta.analytics_configured),
      crmSent: number(meta.crm_sent),
      crmFailed: number(meta.crm_failed),
      crmQueued: number(meta.crm_queued),
      lastCrmSentAt: nullableString(meta.last_crm_sent_at),
    },
    partnerAccess: {
      registrationGateConfigured: Boolean(partnerAccess.registration_gate_configured),
      activePartners: number(partnerAccess.active_partners),
      credentialedPartners: number(partnerAccess.credentialed_partners),
      exactLeadLinksRequireLogin: Boolean(partnerAccess.exact_lead_links_require_login),
    },
    protection: {
      plaintextSecretsReturned: Boolean(protection.plaintext_secrets_returned),
      credentialRotation: String(protection.credential_rotation || ''),
    },
  };
}

async function parseResponse(response: Response): Promise<AdminSettingsSnapshot> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new AdminSettingsApiError(
      body?.error?.message || 'Δεν ήταν δυνατή η φόρτωση των ρυθμίσεων.',
      body?.error?.fields && typeof body.error.fields === 'object' ? body.error.fields : {},
    );
  }
  return normalizeSettings(body?.data);
}

export async function fetchAdminSettings(): Promise<AdminSettingsSnapshot> {
  const response = await fetch('/api/admin/settings.php', {
    credentials: 'include',
    headers: { 'X-CSRF-Token': await getCsrfToken() },
  });
  return parseResponse(response);
}

export async function updateSenderIdentity(input: UpdateSenderIdentityInput): Promise<AdminSettingsSnapshot> {
  const response = await fetch('/api/admin/settings.php', {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await getCsrfToken(),
    },
    body: JSON.stringify({
      sender_name: input.senderName,
      sender_email: input.senderEmail,
    }),
  });
  return parseResponse(response);
}
